import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import RecoveryCodeModal from '@/components/RecoveryCodeModal';
import LegalFooter from '@/components/LegalFooter';
import { track } from '@/lib/analytics';

export default function Auth() {
    const { login, register, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const initialMode = params.get('mode') === 'signup' ? 'signup' : 'signin';

    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [name, setName] = useState('');
    const [resetMethod, setResetMethod] = useState('recovery_code');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [recoveryCode, setRecoveryCode] = useState(null);
    const [pendingNav, setPendingNav] = useState(null);

    useEffect(() => {
        if (user && !recoveryCode) {
            navigate(user.onboarded ? '/app/home' : '/onboarding', { replace: true });
        }
    }, [user, navigate, recoveryCode]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            if (mode === 'signup') {
                const r = await register(email, password, name, resetMethod);
                const u = r.user;
                track('user-signed-up', { reset_method: resetMethod });
                const next = u.onboarded ? '/app/home' : '/onboarding';
                if (r.recovery_code) {
                    setRecoveryCode(r.recovery_code);
                    setPendingNav(next);
                } else {
                    navigate(next, { replace: true });
                }
            } else {
                const u = await login(email, password);
                track('user-signed-in');
                navigate(u.onboarded ? '/app/home' : '/onboarding', { replace: true });
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const dismissRecovery = () => {
        setRecoveryCode(null);
        if (pendingNav) navigate(pendingNav, { replace: true });
    };

    return (
        <div className="min-h-screen bg-background relative overflow-hidden flex flex-col">
            <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-sage/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 -right-40 w-[460px] h-[460px] rounded-full bg-dusty/50 blur-3xl" />

            <div className="grid md:grid-cols-2 flex-1">
            {/* Form pane */}
            <div className="relative z-10 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="inline-flex items-center gap-2 text-forest-soft hover:text-forest text-sm mb-8" data-testid="auth-back-link">
                        <ArrowLeft size={16} /> Back home
                    </Link>
                    <h1 className="font-serif text-4xl text-forest">
                        {mode === 'signup' ? 'Begin your practice' : 'Welcome back'}
                    </h1>
                    <p className="text-forest-soft mt-2">
                        {mode === 'signup' ? 'A few quiet minutes, made just for you.' : 'Take a slow breath. We saved your spot.'}
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="auth-form">
                        {mode === 'signup' && (
                            <div>
                                <label className="text-sm text-forest-soft" htmlFor="name">Your name</label>
                                <input
                                    id="name"
                                    data-testid="auth-name-input"
                                    type="text" required
                                    value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder="What should we call you?"
                                    className="qm-input mt-1"
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="email">Email</label>
                            <input
                                id="email"
                                data-testid="auth-email-input"
                                type="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="qm-input mt-1"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="password">Password</label>
                            <div className="relative mt-1">
                                <input
                                    id="password"
                                    data-testid="auth-password-input"
                                    type={showPassword ? 'text' : 'password'} required minLength={6}
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    className="qm-input pr-12 w-full"
                                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    data-testid="auth-password-toggle"
                                    className="absolute inset-y-0 right-2 my-auto h-9 w-9 grid place-items-center rounded-full text-forest-soft hover:bg-sage/30 hover:text-forest"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {mode === 'signup' && (
                            <fieldset className="rounded-2xl border border-forest/15 px-4 py-3" data-testid="auth-reset-method">
                                <legend className="text-xs uppercase tracking-[0.2em] text-forest-soft px-1">If you forget your password</legend>
                                <label className="flex items-start gap-2 mt-1 text-sm cursor-pointer">
                                    <input
                                        type="radio" name="reset_method" value="recovery_code"
                                        checked={resetMethod === 'recovery_code'}
                                        onChange={() => setResetMethod('recovery_code')}
                                        data-testid="reset-method-recovery"
                                        className="mt-1"
                                    />
                                    <span className="text-forest">
                                        <strong>Recovery code</strong>
                                        <span className="block text-xs text-forest-soft">We’ll show a one-time code next — save it somewhere safe.</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 mt-2 text-sm cursor-pointer">
                                    <input
                                        type="radio" name="reset_method" value="email"
                                        checked={resetMethod === 'email'}
                                        onChange={() => setResetMethod('email')}
                                        data-testid="reset-method-email"
                                        className="mt-1"
                                    />
                                    <span className="text-forest">
                                        <strong>Email reset link</strong>
                                        <span className="block text-xs text-forest-soft">Coming soon — we’ll still give you a recovery code now as backup.</span>
                                    </span>
                                </label>
                            </fieldset>
                        )}

                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="auth-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="auth-submit-button"
                            className="qm-btn-primary w-full disabled:opacity-60"
                        >
                            {submitting ? 'A moment …' : mode === 'signup' ? 'Create my space' : 'Sign in'}
                        </button>
                    </form>

                    <div className="mt-6 text-sm text-forest-soft space-y-2">
                        {mode === 'signup' ? (
                            <div>Already practicing?{' '}
                                <button onClick={() => setMode('signin')} className="text-forest underline" data-testid="auth-toggle-signin">Sign in</button>
                            </div>
                        ) : (
                            <>
                                <div>
                                    New here?{' '}
                                    <button onClick={() => setMode('signup')} className="text-forest underline" data-testid="auth-toggle-signup">Create an account</button>
                                </div>
                                <div>
                                    <Link to="/reset" data-testid="auth-forgot-link" className="text-forest underline">
                                        Forgot your password?
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Visual pane */}
            <div className="relative hidden md:block">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            'linear-gradient(160deg, rgba(168,197,176,0.55), rgba(232,208,208,0.45) 60%, rgba(200,182,219,0.45)), url(https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=1200&q=80)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div className="relative h-full grid place-items-center p-10">
                    <blockquote className="max-w-md text-cream font-serif text-2xl leading-relaxed text-center drop-shadow">
                        “The mind is everything. <br />What you think, you become.”
                        <footer className="mt-4 not-italic text-sm tracking-widest uppercase opacity-90">— Buddha</footer>
                    </blockquote>
                </div>
            </div>
            </div>

            <LegalFooter />

            {recoveryCode && (
                <RecoveryCodeModal
                    code={recoveryCode}
                    email={email}
                    onClose={dismissRecovery}
                />
            )}
        </div>
    );
}
